import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export const CostOverTimeChart = ({ traces = [] }) => {
  // Group traces by hour and calculate cumulative cost
  const chartData = useMemo(() => {
    if (!traces || traces.length === 0) return [];

    // Group by hour
    const hourlyData = {};
    
    traces.forEach((trace) => {
      try {
        const timestamp = new Date(trace.ts_start);
        const hour = `${timestamp.getHours()}:00`;
        
        if (!hourlyData[hour]) {
          hourlyData[hour] = {
            hour,
            cost: 0,
            count: 0,
          };
        }
        
        hourlyData[hour].cost += trace.total_cost || 0;
        hourlyData[hour].count += 1;
      } catch (e) {
        // Skip invalid timestamps
      }
    });

    // Convert to array and sort by hour
    const data = Object.values(hourlyData)
      .map((item) => ({
        ...item,
        cost: parseFloat(item.cost.toFixed(4)),
      }))
      .sort((a, b) => {
        const hourA = parseInt(a.hour.split(':')[0]);
        const hourB = parseInt(b.hour.split(':')[0]);
        return hourA - hourB;
      });

    return data;
  }, [traces]);

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cost Over Time</CardTitle>
          <CardDescription>No trace data available</CardDescription>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center text-muted-foreground">
          No data to display
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cost Over Time</CardTitle>
        <CardDescription>
          Hourly cost distribution • Last 24 hours
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="hour" 
              fontSize={12}
            />
            <YAxis 
              label={{ value: 'Cost (USD)', angle: -90, position: 'insideLeft' }}
              fontSize={12}
            />
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white p-3 border rounded shadow-lg">
                      <p className="font-semibold">{data.hour}</p>
                      <p className="text-sm text-gray-600">Cost: ${data.cost}</p>
                      <p className="text-sm text-gray-600">Traces: {data.count}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="cost" 
              stroke="#10b981" 
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
